import * as React from 'react';
import { Headphones, Mic, Play, RefreshCw, Square, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Select } from '@/components/ui/primitives';

export interface AudioDevicePreferences {
  micId: string;
  speakerId: string;
}

const STORAGE_KEY = 'leadforge.audio-device-preferences.v1';
const DEFAULT_PREFERENCES: AudioDevicePreferences = { micId: '', speakerId: '' };

export function loadAudioDevicePreferences(): AudioDevicePreferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<AudioDevicePreferences>;
    return {
      micId: typeof parsed.micId === 'string' ? parsed.micId : '',
      speakerId: typeof parsed.speakerId === 'string' ? parsed.speakerId : '',
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function saveAudioDevicePreferences(preferences: AudioDevicePreferences): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Browser storage can be disabled. Device selection still works for this tab.
  }
}

function microphoneConstraints(micId: string): MediaTrackConstraints {
  return {
    ...(micId ? { deviceId: { exact: micId } } : {}),
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  };
}

type SinkAudioElement = HTMLAudioElement & {
  setSinkId?: (deviceId: string) => Promise<void>;
};

async function setOutputDevice(element: HTMLAudioElement | null, speakerId: string): Promise<boolean> {
  if (!element || !speakerId) return true;
  const sink = element as SinkAudioElement;
  if (typeof sink.setSinkId !== 'function') return false;
  await sink.setSinkId(speakerId);
  return true;
}

function deviceLabel(device: MediaDeviceInfo, index: number, kind: 'microphone' | 'speaker'): string {
  return device.label || `${kind === 'microphone' ? 'Microphone' : 'Speaker'} ${index + 1}`;
}

interface AudioDeviceSetupProps {
  preferences: AudioDevicePreferences;
  onChange(preferences: AudioDevicePreferences): void;
  callInProgress?: boolean;
}

export function AudioDeviceSetup({ preferences, onChange, callInProgress = false }: AudioDeviceSetupProps) {
  const [open, setOpen] = React.useState(false);
  const [microphones, setMicrophones] = React.useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = React.useState<MediaDeviceInfo[]>([]);
  const [refreshing, setRefreshing] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [meter, setMeter] = React.useState(0);
  const [sampleUrl, setSampleUrl] = React.useState<string | null>(null);
  const [recording, setRecording] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const testStreamRef = React.useRef<MediaStream | null>(null);
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const meterFrameRef = React.useRef<number | null>(null);
  const sampleAudioRef = React.useRef<HTMLAudioElement | null>(null);

  const updatePreferences = React.useCallback((next: AudioDevicePreferences) => {
    saveAudioDevicePreferences(next);
    onChange(next);
  }, [onChange]);

  const refreshDevices = React.useCallback(async (requestPermission = false) => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setError('This browser does not support audio device selection.');
      return;
    }
    setRefreshing(true);
    setError(null);
    let permissionStream: MediaStream | null = null;
    try {
      if (requestPermission) {
        permissionStream = await navigator.mediaDevices.getUserMedia({
          audio: microphoneConstraints(''),
          video: false,
        });
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      setMicrophones(devices.filter((device) => device.kind === 'audioinput'));
      setSpeakers(devices.filter((device) => device.kind === 'audiooutput'));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'LeadForge could not access audio devices.');
    } finally {
      permissionStream?.getTracks().forEach((track) => track.stop());
      setRefreshing(false);
    }
  }, [preferences.micId]);

  const stopMicTest = React.useCallback(async () => {
    if (meterFrameRef.current !== null) cancelAnimationFrame(meterFrameRef.current);
    meterFrameRef.current = null;
    testStreamRef.current?.getTracks().forEach((track) => track.stop());
    testStreamRef.current = null;
    const context = audioContextRef.current;
    audioContextRef.current = null;
    if (context && context.state !== 'closed') await context.close().catch(() => undefined);
    setTesting(false);
    setMeter(0);
  }, []);

  const startMicTest = React.useCallback(async (): Promise<MediaStream | null> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This browser cannot access a microphone.');
      return null;
    }
    await stopMicTest();
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: microphoneConstraints(preferences.micId),
        video: false,
      });
      testStreamRef.current = stream;

      const context = new AudioContext();
      audioContextRef.current = context;
      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);

      const tick = () => {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) {
          const normalized = (sample - 128) / 128;
          sum += normalized * normalized;
        }
        const rms = Math.sqrt(sum / samples.length);
        setMeter(Math.min(1, rms * 5));
        meterFrameRef.current = requestAnimationFrame(tick);
      };
      tick();
      setTesting(true);
      await refreshDevices(false);
      return stream;
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'LeadForge could not open the selected microphone.';
      setError(message);
      return null;
    }
  }, [preferences.micId, refreshDevices, stopMicTest]);

  const recordSample = React.useCallback(async () => {
    if (typeof MediaRecorder === 'undefined') {
      setError('This browser does not support microphone recording tests.');
      return;
    }
    let stream = testStreamRef.current;
    if (!stream) stream = await startMicTest();
    if (!stream) return;

    setRecording(true);
    setError(null);
    try {
      const chunks: BlobPart[] = [];
      const recorder = new MediaRecorder(stream);
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size) chunks.push(event.data);
      });
      const stopped = new Promise<void>((resolve) => recorder.addEventListener('stop', () => resolve(), { once: true }));
      recorder.start();
      await new Promise((resolve) => window.setTimeout(resolve, 3000));
      if (recorder.state !== 'inactive') recorder.stop();
      await stopped;

      const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
      const nextUrl = URL.createObjectURL(blob);
      setSampleUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return nextUrl;
      });
      toast.success('3-second microphone sample recorded. Play it back through the selected speaker.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not record the microphone sample.');
    } finally {
      setRecording(false);
    }
  }, [startMicTest]);

  const playSample = React.useCallback(async () => {
    const audio = sampleAudioRef.current;
    if (!audio) return;
    try {
      const outputSelected = await setOutputDevice(audio, preferences.speakerId);
      if (!outputSelected && preferences.speakerId) {
        toast.warning('This browser cannot force a speaker device. Playback will use the browser default.');
      }
      audio.currentTime = 0;
      await audio.play();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not play the microphone sample.');
    }
  }, [preferences.speakerId]);

  React.useEffect(() => {
    void refreshDevices(false);
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.addEventListener) return;
    const handleChange = () => void refreshDevices(false);
    mediaDevices.addEventListener('devicechange', handleChange);
    return () => mediaDevices.removeEventListener('devicechange', handleChange);
  }, [refreshDevices]);

  React.useEffect(() => {
    if (callInProgress && testing) void stopMicTest();
  }, [callInProgress, stopMicTest, testing]);

  React.useEffect(() => () => {
    if (meterFrameRef.current !== null) cancelAnimationFrame(meterFrameRef.current);
    testStreamRef.current?.getTracks().forEach((track) => track.stop());
    if (audioContextRef.current?.state !== 'closed') void audioContextRef.current?.close().catch(() => undefined);
  }, []);

  React.useEffect(() => () => {
    if (sampleUrl) URL.revokeObjectURL(sampleUrl);
  }, [sampleUrl]);

  const selectedMicMissing = !!preferences.micId && microphones.length > 0 && !microphones.some((device) => device.deviceId === preferences.micId);
  const selectedSpeakerMissing = !!preferences.speakerId && speakers.length > 0 && !speakers.some((device) => device.deviceId === preferences.speakerId);

  return (
    <div className="rounded-lg border border-border bg-muted/20">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-xs font-semibold">
          <Headphones className="h-3.5 w-3.5" />
          Audio setup
        </span>
        <span className="text-2xs text-muted-foreground">
          {preferences.micId || preferences.speakerId ? 'Custom headset selected' : 'Browser defaults'}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-border px-3 py-3">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1">
              <span className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Mic className="h-3 w-3" /> Microphone
              </span>
              <Select
                value={selectedMicMissing ? '' : preferences.micId}
                onChange={(event) => updatePreferences({ ...preferences, micId: event.target.value })}
                disabled={callInProgress || refreshing}
                aria-label="Calling microphone"
              >
                <option value="">Browser default microphone</option>
                {microphones.map((device, index) => (
                  <option key={device.deviceId || `mic-${index}`} value={device.deviceId}>
                    {deviceLabel(device, index, 'microphone')}
                  </option>
                ))}
              </Select>
            </label>

            <label className="space-y-1">
              <span className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Volume2 className="h-3 w-3" /> Speaker / headset
              </span>
              <Select
                value={selectedSpeakerMissing ? '' : preferences.speakerId}
                onChange={(event) => updatePreferences({ ...preferences, speakerId: event.target.value })}
                disabled={callInProgress || refreshing}
                aria-label="Calling speaker"
              >
                <option value="">Browser default speaker</option>
                {speakers.map((device, index) => (
                  <option key={device.deviceId || `speaker-${index}`} value={device.deviceId}>
                    {deviceLabel(device, index, 'speaker')}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void refreshDevices(true)} disabled={callInProgress || refreshing}>
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Checking...' : 'Allow / refresh devices'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void (testing ? stopMicTest() : startMicTest())} disabled={callInProgress}>
              {testing ? <Square className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              {testing ? 'Stop mic meter' : 'Start mic meter'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void recordSample()} loading={recording} disabled={callInProgress || recording}>
              Record 3 sec
            </Button>
            {sampleUrl && (
              <Button variant="outline" size="sm" onClick={() => void playSample()} disabled={callInProgress}>
                <Play className="h-3.5 w-3.5" />
                Play test
              </Button>
            )}
            <audio ref={sampleAudioRef} src={sampleUrl ?? undefined} className="hidden" />
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between text-2xs text-muted-foreground">
              <span>Live microphone level</span>
              <span>{testing ? (meter > 0.04 ? 'Signal detected' : 'Speak into the mic') : 'Start the meter to test'}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary transition-[width] duration-75" style={{ width: `${Math.round(meter * 100)}%` }} />
            </div>
          </div>

          {selectedMicMissing && <p className="text-2xs text-amber-600">The saved microphone is not connected. LeadForge will use the browser default until you choose another one.</p>}
          {selectedSpeakerMissing && <p className="text-2xs text-amber-600">The saved speaker is not connected. LeadForge will use the browser default until you choose another one.</p>}
          {error && <p className="text-xs text-destructive">{error}</p>}
          <p className="text-2xs leading-snug text-muted-foreground">
            These choices are saved only in this browser. LeadForge sends the selected microphone and speaker IDs directly to Telnyx for each WebRTC call.
          </p>
        </div>
      )}
    </div>
  );
}
